import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useToast } from '../components/ui/Toast';
import { toErrorMessage } from '../utils/toErrorMessage';

/**
 * Invalidate a batch of query keys on the shared QueryClient. Used by the
 * domain-specific `invalidate*Lists` helpers across `useTimeOff`,
 * `useExpenses`, `useEmployeeExtraCosts`, and `useCapacity` to DRY up the
 * per-key `queryClient.invalidateQueries({ queryKey })` repetition.
 *
 * Behaviour-preserving: TanStack Query's `invalidateQueries` is fire-and-forget
 * (returns a Promise that's ignored at the call sites) and matches keys by
 * prefix, so iterating with the same keys is identical to inlining the calls.
 */
export function invalidateMany(queryClient: QueryClient, keys: readonly QueryKey[]): void {
  for (const queryKey of keys) {
    queryClient.invalidateQueries({ queryKey });
  }
}

/**
 * Returns a stable error handler `(err, fallback) => void` that extracts a
 * human-readable message and shows it as an error toast. Eliminates the
 * repeated `const toast = useToast()` + `toast(toErrorMessage(err, msg), 'error')`
 * pattern across mutation hooks.
 *
 * Usage:
 *   const onError = useErrorToast();
 *   useMutation({ onError: (err) => onError(err, "Couldn't save") });
 */
export function useErrorToast(): (err: unknown, fallback: string) => void {
  const toast = useToast();
  return (err: unknown, fallback: string) => toast(toErrorMessage(err, fallback), 'error');
}
